import React from 'react';
import Icon from '../../../components/AppIcon';

const TypographyTab = ({ config, onUpdate }) => {
  const fontOptions = [
    { value: 'Inter', label: 'Inter (Recommended)' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Montserrat', label: 'Montserrat' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Source Sans Pro', label: 'Source Sans Pro' }
  ];

  const monoFontOptions = [
    { value: 'JetBrains Mono', label: 'JetBrains Mono (Recommended)' },
    { value: 'Fira Code', label: 'Fira Code' },
    { value: 'Source Code Pro', label: 'Source Code Pro' },
    { value: 'Monaco', label: 'Monaco' },
    { value: 'Consolas', label: 'Consolas' }
  ];

  const typographyStyles = [
    { 
      id: 'h1', 
      label: 'Heading 1', 
      size: config?.fontSizes?.['4xl'] || '2.25rem',
      weight: '700',
      example: 'Dashboard Overview'
    },
    { 
      id: 'h2', 
      label: 'Heading 2', 
      size: config?.fontSizes?.['3xl'] || '1.875rem',
      weight: '600',
      example: 'Biodata Management'
    },
    { 
      id: 'h3', 
      label: 'Heading 3', 
      size: config?.fontSizes?.['2xl'] || '1.5rem',
      weight: '600',
      example: 'User Information'
    },
    { 
      id: 'h4', 
      label: 'Heading 4', 
      size: config?.fontSizes?.xl || '1.25rem',
      weight: '500',
      example: 'Section Title'
    },
    { 
      id: 'body', 
      label: 'Body Text', 
      size: config?.fontSizes?.base || '1rem',
      weight: '400',
      example: 'This is the main body text used throughout the application.'
    },
    { 
      id: 'small', 
      label: 'Small Text', 
      size: config?.fontSizes?.sm || '0.875rem',
      weight: '400',
      example: 'Helper text and secondary information'
    },
    { 
      id: 'caption', 
      label: 'Caption', 
      size: config?.fontSizes?.xs || '0.75rem',
      weight: '400',
      example: 'Timestamps and metadata'
    }
  ];

  const handleFontFamilyChange = (type, value) => {
    onUpdate({
      fontFamily: {
        ...config?.fontFamily,
        [type]: value
      }
    });
  };

  const handleFontSizeChange = (sizeKey, value) => {
    onUpdate({
      fontSizes: {
        ...config?.fontSizes,
        [sizeKey]: value
      }
    });
  };

  return (
    <div className="space-y-8">
      {/* Font Family Selection */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Type" size={20} className="mr-2 text-[#2D9B9B]" />
          Font Families
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">
              Heading Font
            </label>
            <select
              value={config?.fontFamily?.heading || 'Inter'}
              onChange={(e) => handleFontFamilyChange('heading', e.target.value)}
              className="w-full px-4 py-3 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B] transition-colors"
            >
              {fontOptions.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
            <div 
              className="mt-3 p-4 bg-secondary-50 rounded-lg border border-secondary-200"
              style={{ fontFamily: config?.fontFamily?.heading || 'Inter' }}
            >
              <p className="text-xl font-semibold text-text-primary">
                CLAB Biodata System
              </p>
              <p className="text-sm text-text-secondary mt-1">
                Preview of heading font
              </p>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">
              Body Font
            </label>
            <select
              value={config?.fontFamily?.body || 'Inter'}
              onChange={(e) => handleFontFamilyChange('body', e.target.value)}
              className="w-full px-4 py-3 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B] transition-colors"
            >
              {fontOptions.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
            <div 
              className="mt-3 p-4 bg-secondary-50 rounded-lg border border-secondary-200"
              style={{ fontFamily: config?.fontFamily?.body || 'Inter' }}
            >
              <p className="text-base text-text-primary">
                This is sample body text for the CLAB platform. It demonstrates how the selected font will appear in paragraphs and general content.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-text-primary mb-3">
            Monospace Font (Code)
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <select
              value={config?.fontFamily?.mono || 'JetBrains Mono'}
              onChange={(e) => handleFontFamilyChange('mono', e.target.value)}
              className="w-full px-4 py-3 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B] transition-colors"
            >
              {monoFontOptions.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
            <div 
              className="p-4 bg-secondary-900 rounded-lg border border-secondary-200"
              style={{ fontFamily: config?.fontFamily?.mono || 'JetBrains Mono' }}
            >
              <p className="text-sm text-green-400">
                {`const clabUser = {`}
              </p>
              <p className="text-sm text-green-400 ml-4">
                {`role: 'biodata_reviewer',`}
              </p>
              <p className="text-sm text-green-400 ml-4">
                {`status: 'active'`}
              </p>
              <p className="text-sm text-green-400">
                {`};`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Typography Scale */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="AlignLeft" size={20} className="mr-2 text-[#2D9B9B]" />
          Typography Scale
        </h3>
        <div className="bg-surface border border-secondary-200 rounded-xl p-6">
          <div className="space-y-6">
            {typographyStyles.map((style) => (
              <div key={style.id} className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-2">
                    <span className="text-sm font-medium text-text-primary w-24">
                      {style.label}
                    </span>
                    <input
                      type="text"
                      value={style.size}
                      onChange={(e) => {
                        const sizeKey = Object.keys(config?.fontSizes || {}).find(key => 
                          config?.fontSizes?.[key] === style.size
                        );
                        if (sizeKey) {
                          handleFontSizeChange(sizeKey, e.target.value);
                        }
                      }}
                      className="w-20 px-2 py-1 border border-secondary-300 rounded text-sm focus:ring-1 focus:ring-[#2D9B9B] focus:border-[#2D9B9B]"
                    />
                    <span className="text-xs text-text-muted w-16">
                      {style.weight}
                    </span>
                  </div>
                  <p 
                    className="text-text-primary"
                    style={{ 
                      fontSize: style.size,
                      fontWeight: style.weight,
                      fontFamily: style.id.startsWith('h') ? 
                        (config?.fontFamily?.heading || 'Inter') : 
                        (config?.fontFamily?.body || 'Inter')
                    }}
                  >
                    {style.example}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Line Height & Spacing */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Menu" size={20} className="mr-2 text-[#2D9B9B]" />
          Line Height & Spacing
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-surface border border-secondary-200 rounded-xl p-6">
            <h4 className="font-medium text-text-primary mb-4">Line Height Guidelines</h4>
            <div className="space-y-4">
              <div className="p-4 bg-secondary-50 rounded-lg">
                <p className="text-sm font-medium text-text-primary mb-2">Tight (1.2)</p>
                <p className="text-sm text-text-primary" style={{ lineHeight: '1.2' }}>
                  Used for headings and display text where space is limited.
                </p>
              </div>
              <div className="p-4 bg-secondary-50 rounded-lg">
                <p className="text-sm font-medium text-text-primary mb-2">Normal (1.5)</p>
                <p className="text-sm text-text-primary" style={{ lineHeight: '1.5' }}>
                  Default line height for most body text and content areas.
                </p>
              </div>
              <div className="p-4 bg-secondary-50 rounded-lg">
                <p className="text-sm font-medium text-text-primary mb-2">Relaxed (1.75)</p>
                <p className="text-sm text-text-primary" style={{ lineHeight: '1.75' }}>
                  Used for longer paragraphs and content that needs better readability.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-surface border border-secondary-200 rounded-xl p-6">
            <h4 className="font-medium text-text-primary mb-4">Letter Spacing</h4>
            <div className="space-y-4">
              <div className="p-4 bg-secondary-50 rounded-lg">
                <p className="text-sm font-medium text-text-primary mb-2">Tight (-0.025em)</p>
                <p className="text-sm text-text-primary" style={{ letterSpacing: '-0.025em' }}>
                  CLAB BIODATA SYSTEM
                </p>
              </div>
              <div className="p-4 bg-secondary-50 rounded-lg">
                <p className="text-sm font-medium text-text-primary mb-2">Normal (0)</p>
                <p className="text-sm text-text-primary" style={{ letterSpacing: '0' }}>
                  CLAB BIODATA SYSTEM
                </p>
              </div>
              <div className="p-4 bg-secondary-50 rounded-lg">
                <p className="text-sm font-medium text-text-primary mb-2">Wide (0.1em)</p>
                <p className="text-sm text-text-primary" style={{ letterSpacing: '0.1em' }}>
                  CLAB BIODATA SYSTEM
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Typography in Context */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Layout" size={20} className="mr-2 text-[#2D9B9B]" />
          Typography in Context
        </h3>
        <div className="bg-surface border border-secondary-200 rounded-xl p-6">
          <div className="space-y-6">
            {/* Article Example */}
            <div className="p-6 bg-gradient-to-r from-[#2D9B9B]/5 to-[#1A7A7A]/5 rounded-lg">
              <h1 
                className="text-text-primary mb-4"
                style={{ 
                  fontSize: config?.fontSizes?.['4xl'] || '2.25rem',
                  fontWeight: '700',
                  fontFamily: config?.fontFamily?.heading || 'Inter'
                }}
              >
                eBiodata CLAB Platform
              </h1>
              <h2 
                className="text-text-primary mb-3"
                style={{ 
                  fontSize: config?.fontSizes?.['2xl'] || '1.5rem',
                  fontWeight: '600',
                  fontFamily: config?.fontFamily?.heading || 'Inter'
                }}
              >
                Comprehensive Biodata Management System
              </h2>
              <p 
                className="text-text-primary mb-4"
                style={{ 
                  fontSize: config?.fontSizes?.base || '1rem',
                  fontFamily: config?.fontFamily?.body || 'Inter',
                  lineHeight: '1.6'
                }}
              >
                Our platform provides a complete solution for managing biodata across construction and engineering projects. 
                With robust user roles, comprehensive review processes, and advanced filtering capabilities, 
                CLAB ensures efficient workforce management and compliance.
              </p>
              <p 
                className="text-text-secondary"
                style={{ 
                  fontSize: config?.fontSizes?.sm || '0.875rem',
                  fontFamily: config?.fontFamily?.body || 'Inter'
                }}
              >
                Last updated: March 2024 • Version 2.1.0
              </p>
            </div>

            {/* UI Component Example */}
            <div className="p-6 bg-secondary-50 rounded-lg">
              <h3 className="text-lg font-semibold text-text-primary mb-4">UI Component Typography</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <button className="w-full px-4 py-3 bg-gradient-to-r from-[#2D9B9B] to-[#1A7A7A] text-white rounded-lg font-medium">
                    Primary Action Button
                  </button>
                  <button className="w-full px-4 py-3 border border-[#2D9B9B] text-[#2D9B9B] rounded-lg font-medium">
                    Secondary Action Button
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="p-4 bg-surface border border-secondary-200 rounded-lg">
                    <p className="text-sm font-medium text-text-primary mb-1">Input Label</p>
                    <input 
                      type="text" 
                      placeholder="Enter biodata information..."
                      className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B]"
                    />
                    <p className="text-xs text-text-muted mt-1">Helper text for additional context</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Typography Guidelines */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="BookOpen" size={20} className="mr-2 text-[#2D9B9B]" />
          Typography Guidelines
        </h3>
        <div className="bg-gradient-to-r from-[#2D9B9B]/5 to-[#1A7A7A]/5 border border-[#2D9B9B]/20 rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-medium text-text-primary mb-3">Hierarchy</h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li>• Use consistent heading scales</li>
                <li>• Maintain proper contrast ratios</li>
                <li>• Establish clear information hierarchy</li>
                <li>• Limit to 3-4 heading levels</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-text-primary mb-3">Readability</h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li>• Use appropriate line heights</li>
                <li>• Maintain comfortable reading lengths</li>
                <li>• Ensure sufficient white space</li>
                <li>• Consider mobile readability</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-text-primary mb-3">Consistency</h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li>• Use consistent font weights</li>
                <li>• Maintain consistent spacing</li>
                <li>• Apply consistent color usage</li>
                <li>• Follow established patterns</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TypographyTab;